import { AvailableHotelModel } from '@/__generated';
import { HOTEL_CONFIG } from '@/config';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import HotelStar from './HotelStar';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { generateHotelDetailUrl, getHotelSearchRequest } from '@/urls';
import moment from 'moment';

type Props = {
  hotel: AvailableHotelModel;
};

const HotelSimpleItem = ({ hotel }: Props) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const { search } = useLocation();

  const priceHintText = React.useMemo(() => {
    let request = getHotelSearchRequest(search);
    let night = moment(request?.checkoutDate).diff(moment(request?.checkinDate), 'day');
    let rooms = request?.rooms.length || 1;
    return [night > 1 ? `${night} nights` : '1 night', rooms > 1 ? `${rooms} rooms` : `${rooms} room`].join(', ');
  }, [search]);

  const onFocus = () => {
    var mapElm = document.getElementById('hotels-map');
    if (mapElm) {
      mapElm.querySelector(`[data-hotel-id="${hotel.hotelCode}"]`)?.classList?.add('active');
      mapElm.querySelector(`[data-hotel-id="${hotel.hotelCode}"]`)?.parentElement?.parentElement?.classList?.add('z-index-999');
    }
  };
  const onBlur = () => {
    var mapElm = document.getElementById('hotels-map');
    if (mapElm) {
      mapElm.querySelector(`[data-hotel-id="${hotel.hotelCode}"]`)?.classList?.remove('active');
      mapElm.querySelector(`[data-hotel-id="${hotel.hotelCode}"]`)?.parentElement?.parentElement?.classList?.remove('z-index-999');
    }
  };
  const viewOnMap = () => {
    if (window.hotelsMap) {
      window.hotelsMap.panTo({ lat: hotel.location?.lat || 0, lng: hotel.location?.lon || 0 });
    }
  };

  return (
    <div className="col-12 mb-10" key={hotel?.hotelCode}>
      <div className="shadow-2 bg-white rounded-4 border-base border-blue-hover" onMouseEnter={onFocus} onMouseLeave={onBlur}>
        <div className="row x-gap-0">
          <div className="col-4 px-5 py-5">
            <Swiper className="mySwiper" navigation={false}>
              {hotel?.hotelImages?.length ? (
                hotel?.hotelImages?.map((slide, i) => (
                  <SwiperSlide key={i}>
                    <img className="w-100 aspect-ratio-1-1 rounded-4" src={slide} alt="hotel" />
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide>
                  <img className="w-100 aspect-ratio-1-1 rounded-4" src={HOTEL_CONFIG.defaultImage} alt="hotel" />
                </SwiperSlide>
              )}
            </Swiper>
          </div>

          <div className="col-8 px-10 py-10">
            <h3 className="text-18 lh-16 fw-500">{[hotel?.hotelName, hotel?.originalName].join(' - ')}</h3>
            <HotelStar star={hotel.starRating || 0} />
            <div className="d-flex justify-content-between items-center">
              <div className="pt-5">
                <p className="text-14">{hotel?.address}</p>
              </div>
              <button onClick={viewOnMap} className="text-blue-1 text-14 bg-white -link ">
                {t('hotelListPage_ViewOnMap')}
                <i className="icon-location ml-5 text-12" />
              </button>
            </div>
            <div className="row x-gap-10 y-gap-10 pt-5">
              <div className="col-6">
                <div className="text-14 text-light-1 ">{priceHintText}</div>
                <div className="text-22 lh-12 fw-600 mt-5">{formatCurrency(hotel?.cheapestPrice)}</div>
              </div>
              <div className="col-6">
                <Link
                  target="_blank"
                  to={generateHotelDetailUrl(hotel.hotelName, hotel.hotelCode)}
                  className="button -md -dark-1 bg-blue-1 text-white "
                >
                  See Availability <div className="icon-arrow-top-right ml-15"></div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelSimpleItem;
